import React from 'react';

// GLOBAL VARIABLES
import { INTERFACE, RECORD } from 'pathnames.js';

// GLOBAL FUNCTIONS
import { makePath } from 'functions';

// GLOBAL COMPONETNS
import Table from 'parts/table/Table';

// CODES
import {
  AM_PRODUCT_TYPE,
  AM_STATUS,
  FM_STATUS,
  INVENTORY_SOURCE,
  INVENTORY_STATUS,
  INVENTORY_TYPE,
  NEW_USED,
  PURCHASE_LEASE,
  RETAIL_WHOLESALE,
  SA_STATUS,
  SALES_STATUS,
  SALE_SUBTYPE
} from 'codes.js';

// FUNCTIONS
const amFmTimeFormat = children => {
  let index = children.indexOf(' ', children.indexOf(' ') + 1);
  return (
    <>
      {children.substr(0, index + 1)}
      <br />
      {children.substr(index + 1)}
    </>
  );
};

// COLUMNS
export const COLUMNS = {
  active: {
    component: Table.Boolean,
    key: 'is_active',
    label: 'Active',
    className: 'text-center',
    width: 60
  },
  acv: {
    component: Table.Dollars,
    key: 'acv',
    label: 'ACV',
    tooltip: 'Actual Cash Value',
    width: 85
  },
  ageOfVehicle: {
    component: Table.Number,
    key: 'age_of_vehicle',
    label: 'Age',
    tooltip: 'Age of Vehicle',
    width: 45
  },
  amAssignment: {
    key: 'am_assignment_name_last_initial',
    label: 'AM',
    tooltip: 'AM Assignment Name',
    width: 80
  },
  amGross: {
    component: Table.Dollars,
    key: 'am_gross',
    label: 'AM Gross',
    width: 85
  },
  amProduct: {
    component: Table.Code,
    key: 'product_type',
    label: 'Product Type',
    className: 'text-center',
    codes: AM_PRODUCT_TYPE,
    width: 70
  },
  amStatus: {
    component: Table.Code,
    key: 'am_assignment_status',
    icon: 'info',
    tooltip: 'AM Time Status',
    codes: AM_STATUS,
    className: 'text-center',
    width: 30
  },
  amTime: {
    key: 'am_time',
    label: 'AM Time',
    tooltip: {
      keys: [
        {
          label: 'Line 1',
          description: 'Time when AM assignment occurred.'
        },
        {
          label: 'Line 2',
          description: 'Time when F&I Gross was entered.'
        }
      ]
    },
    format: amFmTimeFormat,
    width: 70
  },
  backendGross: {
    component: Table.Dollars,
    key: 'backend_gross',
    label: 'BE Gross',
    tooltip: 'Backend Gross',
    width: 85
  },
  backendGrossAdjust: {
    component: Table.Dollars.ColorCoded,
    key: 'backend_gross_adj',
    label: 'BE Gross Adjust',
    tooltip: 'Backend Gross Adjust',
    width: 85
  },
  backendGrossBP: {
    component: Table.Dollars,
    key: 'backend_gross_before_pack',
    label: 'BE Gross BP',
    tooltip: 'Backend Gross Before Pack',
    width: 85
  },
  backendGrossNew: {
    component: Table.Dollars,
    key: 'backend_gross_new',
    label: 'New BE Gross',
    tooltip: 'New Backend Gross',
    width: 85
  },
  customer: (rootPath = INTERFACE.sales, IDKey = 'sales_id') => ({
    component: Table.Assignment,
    key: 'customer1_full_name',
    key2: 'customer2_full_name',
    label: 'Customer',
    to: (_value, { [IDKey]: recordID }) => makePath(rootPath, RECORD.view, recordID),
    width: 110
  }),
  dealerPack: {
    component: Table.Dollars,
    key: 'dealer_pack_total',
    name: 'Dealer Pack',
    label: 'Dealer Pack',
    width: 95
  },
  deliveredAge: {
    component: Table.Number,
    key: 'delivered_age',
    label: 'DAge',
    tooltip: 'Delivered Age',
    width: 50
  },
  deliveredDate: {
    component: Table.Date,
    key: 'delivered_date',
    label: 'DDate',
    tooltip: 'Delivered Date',
    width: 65
  },
  dmsInvoice: {
    component: Table.Dollars,
    key: 'dms_invoice',
    label: 'DMS Inv',
    tooltip: 'DMS Invoice',
    width: 85
  },
  fiGross: {
    component: Table.Dollars,
    key: 'fi_gross',
    label: 'F&I Gross',
    width: 85
  },
  fmAssignment: {
    key: 'fm_assignment_name_last_initial',
    label: 'FM',
    tooltip: 'FM Assignment Name',
    width: 80
  },
  fmGross: {
    component: Table.Dollars,
    key: 'fm_gross',
    label: 'FM Gross',
    width: 85
  },
  fmManager: {
    component: Table.Assignment,
    key: 'fm_manager',
    label: 'F&I Manager',
    width: 80
  },
  fmStatus: {
    component: Table.Code,
    key: 'fm_assignment_status',
    icon: 'info',
    tooltip: 'FM Time Status',
    codes: FM_STATUS,
    className: 'text-center',
    headerClassName: 'justify-content-center',
    width: 30
  },
  fmTime: {
    key: 'fm_time',
    label: 'FM Time',
    tooltip: {
      keys: [
        {
          label: 'Line 1',
          description: 'Time when FM assignment occurred.'
        },
        {
          label: 'Line 2',
          description: 'Time when F&I Gross was entered.'
        }
      ]
    },
    width: 70
  },
  frontendGross: {
    component: Table.Dollars,
    key: 'frontend_gross',
    label: 'FE Gross',
    tooltip: 'Frontend Gross',
    width: 95
  },
  frontendGrossAdjust: {
    component: Table.Dollars.ColorCoded,
    key: 'frontend_gross_adj',
    label: 'FE Gross Adjust',
    tooltip: 'Frontend Gross Adjust',
    width: 85
  },
  frontendGrossAP: {
    component: Table.Dollars,
    key: 'frontend_gross_after_pack',
    label: 'FE Gross AP',
    tooltip: 'Frontend Gross After Pack',
    width: 85
  },
  frontendGrossBP: {
    component: Table.Dollars,
    key: 'frontend_gross_before_pack',
    label: 'FE Gross BP',
    tooltip: 'Frontend Gross Before Pack',
    width: 85
  },
  frontendGrossNew: {
    component: Table.Dollars,
    key: 'frontend_gross_new',
    label: 'New FE Gross',
    tooltip: 'New Frontend Gross',
    width: 85
  },
  fundedDate: {
    component: Table.Date,
    key: 'funded_date',
    label: 'FDate',
    tooltip: 'Funded Date',
    width: 65
  },
  global: {
    component: Table.Boolean,
    key: 'is_global',
    label: 'Global',
    className: 'text-center',
    width: 60
  },
  inventorySource: {
    component: Table.Code,
    key: 'inventory_source_code',
    label: 'SRC',
    tooltip: 'Inventory Source',
    codes: INVENTORY_SOURCE,
    className: 'text-center',
    width: 45
  },
  inventoryStatus: {
    component: Table.Code,
    key: 'inventory_status',
    label: 'ST',
    tooltip: 'Inventory Status',
    codes: INVENTORY_STATUS,
    badge: false,
    className: 'text-center',
    width: 35
  },
  inventoryType: {
    component: Table.Code,
    key: 'inventory_type',
    icon: 'info',
    tooltip: 'Inventory Type',
    codes: INVENTORY_TYPE
  },
  makeModel: {
    key: 'make_model',
    label: 'Vehicle',
    width: 100
  },
  mileage: {
    component: Table.Number,
    key: 'mileage',
    label: 'Miles',
    tooltip: 'Total Mileage',
    width: 60
  },
  newUsed: {
    component: Table.Code,
    key: 'new_used',
    label: 'N/P',
    tooltip: 'New/Pre-Owned',
    codes: NEW_USED,
    width: 45
  },
  purchasedDate: {
    component: Table.Date,
    key: 'sales_time',
    label: 'PDate',
    tooltip: 'Purchased Date',
    width: 65
  },
  purchaseLease: {
    component: Table.Code,
    key: 'purchase_lease',
    label: 'P/L',
    tooltip: 'Purchase/Lease',
    codes: PURCHASE_LEASE,
    width: 45
  },
  retailWholesale: {
    component: Table.Code,
    key: 'sale_type_code',
    label: 'R/W',
    tooltip: 'Retail/Wholesale',
    codes: RETAIL_WHOLESALE,
    width: 45
  },
  sa1: {
    component: Table.Code,
    key: 'sa1',
    label: 'SA1',
    tooltip: 'Factory or Purchase Invoice',
    codes: SA_STATUS,
    className: 'text-center',
    width: 35
  },
  sa2: {
    component: Table.Code,
    key: 'sa2',
    label: 'SA2',
    tooltip: 'Executed Deal Package',
    codes: SA_STATUS,
    className: 'text-center',
    width: 35
  },
  sa3: {
    component: Table.Code,
    key: 'sa3',
    label: 'SA3',
    tooltip: 'Accounting Invoice / Bill of Sale',
    codes: SA_STATUS,
    className: 'text-center',
    width: 35
  },
  salesManager: {
    component: Table.Assignment,
    key: 'salesmanager1',
    key2: 'salesmanager2',
    label: 'Sales Manager',
    width: 80
  },
  salesPerson: {
    component: Table.Assignment,
    key: 'sales_man1_full_name',
    key2: 'sales_man2_full_name',
    label: 'Salesperson',
    width: 95
  },
  salesStatus: {
    component: Table.Code,
    key: 'sales_status_code',
    icon: 'info',
    tooltip: 'Sales Status',
    codes: SALES_STATUS,
    headerClassName: 'justify-content-center',
    width: 45
  },
  salesTime: {
    key: 'sales_time',
    label: 'Time',
    tooltip: 'Time when deal was entered into the system.',
    width: 70
  },
  saleSubtype: {
    component: Table.Code,
    key: 'sale_subtype_code',
    label: 'SS',
    tooltip: 'Sale Subtype',
    codes: SALE_SUBTYPE,
    width: 55
  },
  stockNumber: {
    key: 'stock_num',
    label: 'Stock #',
    to: (_children, { daily_sales_inventory_id } = {}) =>
      makePath(INTERFACE.inventory, RECORD.view, daily_sales_inventory_id),
    width: 110
  },
  totalGross: {
    component: Table.Dollars,
    key: 'total_gross',
    label: 'Total Gross',
    width: 85
  },
  totalGrossBP: {
    component: Table.Dollars,
    key: 'total_fe_be_gross_before_pack',
    label: 'Total FE & BE Gross BP',
    tooltip: 'Total Frontend & Backend Gross Before Pack',
    width: 95
  },
  totalGrossAP: {
    component: Table.Dollars,
    key: 'total_fe_be_gross_after_pack',
    label: 'Total FE & BE Gross AP',
    tooltip: 'Total Frontend & Backend Gross After Pack',
    width: 95
  },
  trades: {
    component: Table.Trade,
    key: 'arrTrades',
    label: 'Trades',
    width: 120
  },
  updatedBy: {
    key: 'updated_by',
    label: 'Updated By',
    width: 110
  },
  updatedOn: {
    component: Table.DateTime,
    key: 'date_updated',
    label: 'Updated On',
    width: 125
  },
  vinNumber: {
    key: 'vin_num',
    label: 'VIN #',
    width: 150
  }
};

export const ACTIONS = {
  edit: IDKey => ({
    icon: 'edit',
    label: 'Edit',
    to: ({ [IDKey]: recordID }) => makePath(window.location.pathname, RECORD.edit, recordID),
    width: 70
  }),
  open: (rootPath, IDKey) => ({
    icon: {
      use: 'folder',
      hover: 'folder-open'
    },
    to: ({ [IDKey]: recordID }) => makePath(rootPath, RECORD.view, recordID),
    width: 40
  })
};
